import { ReactNode } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Bell, Search, LogOut } from "lucide-react";

interface DashboardLayoutProps {
  children: ReactNode;
  sidebarItems: { label: string; path: string; icon: ReactNode }[];
  currentPath: string;
  userName?: string;
  userPhone?: string;
}

export default function DashboardLayout({
  children,
  sidebarItems,
  currentPath,
  userName = "User",
  userPhone = "",
}: DashboardLayoutProps) {
  const navigate = usePrefixedNavigate();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (_error) {
      // Error already handled in signOut
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col border-r border-border bg-white">
        <div className="border-b border-border p-6">
          <h2 className="text-xl font-bold text-[#8B0000]">JBF Portal</h2>
        </div>
        <nav className="flex-1 space-y-2 p-4">
          {sidebarItems.map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 transition-colors ${
                currentPath === item.path
                  ? "bg-[#8B0000] text-white"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex flex-1 flex-col">
        {/* Top Header */}
        <header className="border-b border-border bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex max-w-md flex-1 items-center gap-4">
              <div className="mr-4 text-xl font-bold text-[#8B0000]">
                JBF Portal
              </div>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  className="bg-background pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium">{userName}</p>
                {userPhone && (
                  <p className="text-xs text-muted-foreground">{userPhone}</p>
                )}
              </div>
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleSignOut}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
}
